"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { calculateFamilyRefrigeratorSize, FAMILY_REFRIGERATOR_SIZE_DEFAULTS } from '@/lib/tools/family-refrigerator-size'
import { FAMILY_OF_FOUR_SIZE } from '@/lib/refrigerator-answer-facts'

const FALLBACK_RELATED = [
  "refrigerator-size-calculator",
  "energy-cost-calculator",
  "food-storage-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const OPENING_RESULT = (() => {
  const r = calculateFamilyRefrigeratorSize(FAMILY_REFRIGERATOR_SIZE_DEFAULTS)
  if (!r) throw new Error('the family size calculator rejected its own opening inputs')
  return r
})()

const META = {
  name: "Family Refrigerator Size Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Size a fridge for the actual humans living in your house — adults, kids, how often you cook, how often you shop. It starts from the common rule of thumb of 4–6 cu ft per adult, then adjusts for cooking and shopping habits.",
  faqs: [
    // FRIDGE-1435: the site's one family-of-four answer, then this calculator's own
    // result for its opening inputs, computed, never typed.
    { question: "How many cubic feet does a family of four need?", answer: `${FAMILY_OF_FOUR_SIZE.answer} This calculator counts each child as 0.7 of an adult, so two adults and two children who cook daily and shop weekly come to about ${OPENING_RESULT.recommendedCapacity} cu ft here (${OPENING_RESULT.minCapacity}–${OPENING_RESULT.maxCapacity} cu ft). Shopping every two weeks or monthly raises the estimate.` },
    { question: "How are children counted?", answer: "Children eat less than adults but still need storage for snacks, lunches, and beverages. This calculator weights each child as 0.7 of an adult — a common rule of thumb in appliance sizing guides." },
    { question: "Why does shopping frequency matter?", answer: "If you shop weekly, a fridge only needs to hold seven days of food. Biweekly shoppers need ~30% more space; monthly bulk shoppers need ~60% more — and usually a separate freezer too." },
    { question: "Should I oversize the fridge?", answer: "A modest 10–15% buffer is sensible for guests, leftovers, and seasonal produce. Oversizing more than that wastes electricity, since refrigerators run 24/7 and use the most energy in a typical kitchen." },
    { question: "What style of fridge fits a family of four?", answer: "French door and side-by-side configurations, which this calculator suggests from 20 to 26 cu ft, suit most families of four. Top-freezer models above 22 cu ft are uncommon." },
    { question: "Is this calculator more accurate than the basic size calculator?", answer: "It's tuned for households with both adults and children, and weights kids appropriately. For mixed-adult households or solo cooks, the standard refrigerator-size-calculator is a better fit." },
  ],
  relatedTools: ["refrigerator-size-calculator", "food-storage-calculator", "energy-cost-calculator"],
}

export function FamilySizeCapacityCalculatorPageClient() {
  const [adults, setAdults] = useState<string>(FAMILY_REFRIGERATOR_SIZE_DEFAULTS.adults)
  const [children, setChildren] = useState<string>(FAMILY_REFRIGERATOR_SIZE_DEFAULTS.children)
  const [cookingFrequency, setCookingFrequency] = useState<string>(FAMILY_REFRIGERATOR_SIZE_DEFAULTS.cookingFrequency)
  const [shoppingFrequency, setShoppingFrequency] = useState<string>(FAMILY_REFRIGERATOR_SIZE_DEFAULTS.shoppingFrequency)

  // The calculation lives in lib/tools/family-refrigerator-size.ts (FRIDGE-1435) so
  // the FAQ below can quote its result for the opening inputs.
  const result = useMemo(
    () => calculateFamilyRefrigeratorSize({ adults, children, cookingFrequency, shoppingFrequency }),
    [adults, children, cookingFrequency, shoppingFrequency]
  )

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Size for the family"
      heroTitleItalic="you actually"
      heroTitlePost="have."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "4–6 cu ft per adult"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "4–6", label: "Cu ft / adult", note: "Rule of thumb" },
            { value: "0.7", label: "Child weighting", note: "Per kid" },
            { value: "+30%", label: "Biweekly shop", note: "Adjustment" },
            { value: "+60%", label: "Monthly bulk", note: "Adjustment" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "family-refrigerator-size-calculator")}
      cta={{
        title: "Shop fridges sized for real families.",
        body: "Browse French door, side-by-side, and bottom-freezer refrigerators, and compare each model's listed capacity with the size you calculated.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Adults, kids,
                <br />
                cook habits, shop habits.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Children weighted at 0.7 of an adult. Cooking and shopping frequency adjust the base capacity.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Adults
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={adults}
                    onChange={(e) => setAdults(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Children
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={children}
                    onChange={(e) => setChildren(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  />
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Each child counts as 0.7 of an adult.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Cooking frequency
                  </label>
                  <select
                    value={cookingFrequency}
                    onChange={(e) => setCookingFrequency(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="rarely">Rarely (eat out mostly)</option>
                    <option value="sometimes">Sometimes</option>
                    <option value="daily">Daily</option>
                    <option value="multiple">Multiple times a day</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Grocery shopping frequency
                  </label>
                  <select
                    value={shoppingFrequency}
                    onChange={(e) => setShoppingFrequency(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="daily">Daily / as needed</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 weeks</option>
                    <option value="monthly">Monthly bulk</option>
                  </select>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Recommended capacity
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {result?.recommendedCapacity ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      cu ft
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.fridgeType ?? "Enter your household details."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Min size
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.minCapacity ?? "—"} cu ft
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        Freezer size
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.freezerSize ?? "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop the size"
          title="French door fridges for families"
          description="The 20–26 cu ft sweet spot most families of four end up in."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                The math starts at <em className="italic text-[#ff9951]">4–6 cu ft per adult.</em> Adjusted for kids.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                The starting point is the common rule of thumb of 4–6 cubic feet per adult. This calculator
                uses 5 per person, weights each child at 0.7 of an adult, and applies the cooking and shopping multipliers stated on this page.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "People-weighted base",
                  d: "5 cu ft per adult, plus 3.5 cu ft per child (children weighted as 0.7 of an adult).",
                },
                {
                  n: "02",
                  t: "Cooking adjustment",
                  d: "×0.8 rarely, ×1.0 sometimes, ×1.2 daily, ×1.4 multiple times a day.",
                },
                {
                  n: "03",
                  t: "Shopping adjustment",
                  d: "×0.8 daily, ×1.0 weekly, ×1.3 biweekly, ×1.6 monthly bulk.",
                },
                {
                  n: "04",
                  t: "Style recommendation",
                  d: "<14 cu ft → top-freezer / compact. 14–20 → standard. 20–26 → French door or side-by-side. 26+ → large French door.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side fridges for narrow kitchens"
          description="Slimmer footprint with eye-level freezer access — capacities up to 28 cu ft."
          collectionType="side-by-side"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
